import { ButtonSpinner } from "@/components/ButtonSpinner";
import { Loading } from "@/components/portal/Loading";
import { Button } from "@/components/ui/button";
import { Quill } from "@/crud/form/Quill/Quill";
import yup from "@/crud/yup-extended";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import { AccreditationGroup } from "@/models/accreditation";
import { Email, EmailThread } from "@/models/email";
import { useAccreditationStore } from "@/store/accreditation";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";
import { Formik, FormikValues } from "formik";
import {
  CornerUpLeft,
  Info,
  SendIcon,
  ChevronDown,
  ChevronUp,
  Eye,
  MousePointerClick,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";

interface Props {
  group: AccreditationGroup;
  thread: EmailThread;
}

const statusColors: Record<Email["status"], string> = {
  Pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  Sent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  Delivered:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Bounce: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  Failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  Received: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export const OpenClickCounts = ({ email }: { email: Email }) => {
  return (
    <div className="flex items-center gap-2 mt-1">
      <span
        className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
          statusColors[email.status]
        }`}
      >
        {email.status}
      </span>
      <span className="inline-flex items-center rounded bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
        <Eye className="size-3.5 mr-1 text-blue-400" />
        {email.openCount}
        <span className="ml-1 text-gray-400">opened</span>
      </span>
      <span className="inline-flex items-center rounded bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
        <MousePointerClick className="size-3.5 mr-1 text-green-400" />
        {email.clickCount}
        <span className="ml-1 text-gray-400">clicked</span>
      </span>
    </div>
  );
};

// Utility to get first line of plain text from HTML
function getFirstLine(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return text.split("\n")[0].slice(0, 100); // limit to 100 chars
}

export const ViewEmail = ({ group, thread }: Props) => {
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const { replyToEmailOpen, setReplyToEmailOpen } = useAccreditationStore();
  const isDarkMode = useDarkMode();
  const { data: emails, isLoading } = useQuery<Email[]>(
    ["emails", group.id, thread.id],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/accreditation/groups/${group.id}/emails/${thread.id}`
      );
      return response.data;
    }
  );

  const sendReplyMutation = useMutation(
    async (values: FormikValues) => {
      const response = await axios.post(
        `${BASE_URL}/accreditation/groups/${group.id}/emails/${thread.id}/reply`,
        values
      );
      return response.data;
    },
    {
      onSuccess: () => {
        setIsSending(false);
        setReplyToEmailOpen(false);
        queryClient.invalidateQueries(["groupEmails"]);
        queryClient.invalidateQueries(["emails"]);
      },
      onError: () => {
        toast.error("Failed to send reply");
        setIsSending(false);
      },
    }
  );

  const [showAll, setShowAll] = useState(false);
  const [openIndexes, setOpenIndexes] = useState<{ [k: number]: boolean }>({});

  const toggleEmail = (idx: number) => {
    setOpenIndexes((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Collapsed/expanded email row (used for both preview and expanded)
  const EmailRow = ({
    email,
    realIdx,
    isOpen,
    toggleEmail,
  }: {
    email: Email;
    realIdx: number;
    isOpen: boolean;
    toggleEmail: () => void;
  }) => {
    return (
      <div key={email.id} className="p-6">
        <div
          className="flex items-start justify-between cursor-pointer"
          onClick={toggleEmail}
        >
          <div>
            <h4>
              {email.type == "Outbound" ? (
                <div className="flex items-center text-sm font-medium text-gray-900 dark:text-gray-100">
                  hello@ticketr.events
                  <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20">
                    Outbound
                  </span>
                </div>
              ) : (
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {email.fromEmail}
                  <span className="ml-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30">
                    Inbound
                  </span>
                </div>
              )}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              to {email.type == "Inbound" ? "hello@ticketr.events" : email.to}
            </p>
            {email.type == "Outbound" && <OpenClickCounts email={email} />}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {format(new Date(email.createdAt), "E dd MMM, HH:mm")},{" "}
            {formatDistanceToNow(new Date(email.createdAt), {
              addSuffix: true,
            })}
          </div>
        </div>
        {isOpen ? (
          <>
            <div className="prose dark:prose-invert max-w-none mt-3">
              <div dangerouslySetInnerHTML={{ __html: email.body }}></div>
            </div>
          </>
        ) : (
          <div className="flex justify-between">
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {getFirstLine(email.body)}
            </div>
            <button
              className="flex items-center text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                toggleEmail();
              }}
            >
              {isOpen ? (
                <>
                  <ChevronUp className="size-4 mr-1" />
                  Hide message
                </>
              ) : (
                <>
                  <ChevronDown className="size-4 mr-1" />
                  Show message
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full">
      {!isLoading && emails ? (
        <div className="grid divide-y dark:divide-white/10">
          {/* Always show the first email as collapsed preview */}
          {emails.length > 0 && (
            <div className="">
              <EmailRow
                key={emails[0].id}
                email={emails[0]}
                realIdx={0}
                isOpen={openIndexes[0]}
                toggleEmail={() => toggleEmail(0)}
              />
            </div>
          )}

          {/* If more than 2 emails, show "N more" button between first and last */}
          {emails.length > 2 && !showAll && (
            <div className="flex justify-center py-2">
              <button
                className="flex items-center text-xs px-3 py-1 rounded-full bg-zinc-200 dark:bg-zinc-800 border dark:border-white/10 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                onClick={() => setShowAll(true)}
              >
                <ChevronDown className="size-4 mr-1" />
                {emails.length - 2} more
              </button>
            </div>
          )}

          {/* Show all emails (except last) as collapsed previews with toggles when expanded */}
          {showAll &&
            emails.slice(1, emails.length - 1).map((email, idx) => {
              const realIdx = idx + 1;
              const isOpen = !!openIndexes[realIdx];
              return (
                <EmailRow
                  realIdx={realIdx}
                  key={email.id}
                  email={email}
                  isOpen={isOpen}
                  toggleEmail={() => toggleEmail(realIdx)}
                />
              );
            })}

          {/* Always show the most recent email at the bottom */}
          {emails.length > 1 && (
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h4>
                    {emails[emails.length - 1].type == "Outbound" ? (
                      <div className="flex items-center text-sm font-medium text-gray-900 dark:text-gray-100">
                        hello@ticketr.events
                        <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20">
                          Outbound
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {emails[emails.length - 1].fromEmail}
                        <span className="ml-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30">
                          Inbound
                        </span>
                      </div>
                    )}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    to{" "}
                    {emails[emails.length - 1].type == "Inbound"
                      ? "hello@ticketr.events"
                      : emails[emails.length - 1].to}
                  </p>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {format(
                    new Date(emails[emails.length - 1].createdAt),
                    "E dd MMM, HH:mm"
                  )}
                  ,{" "}
                  {formatDistanceToNow(
                    new Date(emails[emails.length - 1].createdAt),
                    {
                      addSuffix: true,
                    }
                  )}
                </div>
              </div>
              {emails[emails.length - 1].type == "Outbound" && (
                <OpenClickCounts email={emails[emails.length - 1]} />
              )}
              <div className="prose dark:prose-invert max-w-none mt-3">
                <div
                  dangerouslySetInnerHTML={{
                    __html: emails[emails.length - 1].body,
                  }}
                ></div>
              </div>
            </div>
          )}

          {emails.length > 2 && showAll && (
            <div className="flex justify-center py-2">
              <button
                className="flex items-center text-xs px-3 py-1 rounded-full bg-zinc-200 dark:bg-zinc-800 border dark:border-white/10 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                onClick={() => setShowAll(false)}
              >
                <ChevronUp className="size-4 mr-1" />
                Collapse
              </button>
            </div>
          )}

          {/* Reply button and form */}
          {!replyToEmailOpen && (
            <div className="p-6">
              <Button
                variant="secondary"
                onClick={() => setReplyToEmailOpen(true)}
              >
                <CornerUpLeft className="size-4 mr-1" />
                Reply
              </Button>
            </div>
          )}

          {replyToEmailOpen && (
            <div className="p-6">
              <div className="sm:grid grid-cols-2 mb-3">
                <div className="dark:text-zinc-400/90 text-sm flex items-center">
                  From
                </div>
                <div className="sm:col-span-1 flex items-center justify-end">
                  <div className="mr-1.5 inline-flex items-center rounded-lg bg-gray-400/10 px-2 py-1 text-xs text-gray-400 ring-1 ring-inset ring-gray-400/20">
                    <img
                      src="https://ticketr.events/favicon-32x32.png"
                      className="size-4 mr-1 rounded-full"
                    />
                    hello@ticketr.events
                  </div>
                  {/* todo: add tooltip to say if you want to change this, reach out to the account manager */}
                  <Info className="inline size-4 dark:text-zinc-500" />
                </div>
              </div>
              <Formik
                initialValues={{ body: "" }}
                validationSchema={yup.object({
                  body: yup.string().required("Reply is required"),
                })}
                onSubmit={async (values: FormikValues) => {
                  setIsSending(true);
                  await sendReplyMutation.mutateAsync(values);
                }}
              >
                {({ handleSubmit }) => (
                  <form>
                    <Quill name="body" />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSubmit()}
                      type="button"
                      className="mt-4"
                    >
                      {isSending ? (
                        <>
                          <ButtonSpinner
                            colour={isDarkMode ? "black" : "white"}
                          />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send email
                          <SendIcon className="size-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </Formik>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <Loading colour={isDarkMode ? "white" : "black"} />
        </div>
      )}
    </div>
  );
};
